import { useEffect, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

type Protocol = 'tcp' | 'udp' | 'icmp' | 'all';

type SecurityRule = {
  protocol: Protocol;
  cidr: string;
  port?: string;
  description?: string;
};

// ingress/egress are always empty now that only an existing NSG can be
// selected — kept in the shape so the oci:create-request action (which still
// expects `securityRules.{ingress,egress}`) receives well-formed arrays.
export type NetworkSecurityValue = {
  mode: 'existing';
  existingNsgId?: string;
  ingress: SecurityRule[];
  egress: SecurityRule[];
};

export const EMPTY_NETWORK_SECURITY_VALUE: NetworkSecurityValue = {
  mode: 'existing',
  ingress: [],
  egress: [],
};

export function OciSecurityRulesBuilderComponent({
  onChange,
  formData,
  formContext,
}: any) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const fd = (formContext as any)?.formData ?? {};
  const region: string = fd.region ?? '';
  const compartmentId: string = fd.compartmentId ?? '';
  const vcnId: string = fd.vpcId ?? '';

  const value: NetworkSecurityValue = formData ?? EMPTY_NETWORK_SECURITY_VALUE;

  const [nsgs, setNsgs] = useState<{ id: string; displayName: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!formData) {
      onChange(EMPTY_NETWORK_SECURITY_VALUE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!region || !compartmentId) {
      return;
    }
    setLoading(true);
    setError('');
    discoveryApi.getBaseUrl('infrastructure').then(baseUrl => {
      const query = new URLSearchParams({ region, compartmentId });
      if (vcnId) query.set('vcnId', vcnId);
      fetchApi
        .fetch(`${baseUrl}/oci/network-security-groups?${query.toString()}`)
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setError(
              typeof data.error === 'string'
                ? data.error
                : data.error?.message ??
                    'Failed to load network security groups',
            );
          } else {
            setNsgs(data.nsgs ?? []);
          }
        })
        .catch(() => setError('Failed to load network security groups'))
        .finally(() => setLoading(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, compartmentId, vcnId]);

  if (!region || !compartmentId || !vcnId) {
    return (
      <FormHelperText>
        Select a region, compartment and VCN first
      </FormHelperText>
    );
  }

  if (loading) return <CircularProgress size={20} />;
  if (error) return <FormHelperText error>{error}</FormHelperText>;

  return (
    <FormControl fullWidth>
      <InputLabel>Network Security Group</InputLabel>
      <Select
        value={value.existingNsgId ?? ''}
        onChange={e =>
          onChange({ ...value, existingNsgId: e.target.value as string })
        }
      >
        {nsgs.length === 0 && (
          <MenuItem value="" disabled>
            No network security groups in this compartment
          </MenuItem>
        )}
        {nsgs.map(nsg => (
          <MenuItem key={nsg.id} value={nsg.id}>
            {nsg.displayName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export const OciSecurityRulesBuilderField = createFormField({
  name: 'OciSecurityRulesBuilder',
  component: OciSecurityRulesBuilderComponent,
});
